import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import * as Sentry from '@sentry/browser';
import {
  CognitoUserSession,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoAccessToken,
} from 'amazon-cognito-identity-js';
import type Model from '@ember-data/model';
import type SessionService from 'scaleflow/services/session';
import type StoreService from 'scaleflow/services/store';
import type TrackerService from 'scaleflow/services/tracker';
import { authDetails } from 'scaleflow/utils/auth-session';
import extractTenantPermissionScopes from 'scaleflow/utils/extract-tenant-permission-scopes';

/** The slice of the `person` record this service reads. */
interface CurrentUserRecord {
  id?: string;
  name?: string;
  abbreviation?: string;
  color?: string;
  favoriteTeams?: unknown;
  favoriteInitiatives?: unknown;
  get?(key: string): unknown;
}

interface FavoriteRef {
  type: string;
  id: string;
}

interface PersonAdapter {
  claimUser(id: string): Promise<unknown>;
}

const loggedOutUser: CurrentUserRecord = { id: undefined };
export default class CurrentUserService extends Service {
  @service declare store: StoreService;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare tracker: TrackerService;

  @tracked _currentUser: CurrentUserRecord = loggedOutUser;
  adapter!: PersonAdapter;

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.adapter = this.store.adapterFor('person') as unknown as PersonAdapter;
  }

  get id() {
    return this.currentUser?.id ?? undefined;
  }

  get name() {
    return this.currentUser?.name;
  }

  get abbreviation() {
    return this.currentUser?.abbreviation;
  }

  get email() {
    return authDetails(this.session)?.email ?? undefined;
  }

  get tenantId() {
    return this.session?.activeTenantId;
  }

  // Computed on every read from the (shared, tenant-agnostic) access token's
  // scope claim, filtered by this tab's own active tenant -- never persisted,
  // so it can't reflect a tenant this tab isn't actually acting as.
  get permissions() {
    const accessToken = authDetails(this.session)?.accessToken;
    const tenantId = this.session?.activeTenantId;
    if (!accessToken || !tenantId) return [];
    const scope =
      new CognitoAccessToken({ AccessToken: accessToken }).payload?.['scope'] ??
      '';
    return extractTenantPermissionScopes(scope, tenantId);
  }

  get profileColor() {
    return this.currentUser?.color;
  }

  get cognito_sub() {
    return authDetails(this.session)?.sub ?? undefined;
  }
  /**
   * Set the current user and set context on Sentry logs
   *
   * @remarks
   * We on purpose don't set any username or email. Sentry can't remove that data once it's logged
   * And due to "the right to be forgotten" - we don't want to spill emails and usernames into places
   * where we can't remove them.
   */
  set currentUser(value: CurrentUserRecord) {
    if (value.id != undefined) {
      this._currentUser = value;
      Sentry.setUser({ id: value.id });
      const user_properties = {
        name: value.name,
        email: this.email,
        tenant_id: this.tenantId,
        cognito_sub: this.cognito_sub,
      };
      this.tracker.setUserId(value.id, user_properties);
    } else {
      this._currentUser = loggedOutUser;
      Sentry.setUser(null);
      this.tracker.reset();
    }
  }

  get currentUser(): CurrentUserRecord {
    return this._currentUser;
  }

  /**
   * This session object is generated using cognito credentials. This can be manually set as credentials for the Amplify Auth packge,
   *  which will allow other amplify services like Storage etc to be used without issus
   */
  get currentSession() {
    const { idToken, refreshToken, accessToken } =
      authDetails(this.session) ?? {};
    if (!idToken || !refreshToken || !accessToken) return null;
    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({
        IdToken: idToken,
      }),
      RefreshToken: new CognitoRefreshToken({
        RefreshToken: refreshToken,
      }),
      AccessToken: new CognitoAccessToken({
        AccessToken: accessToken,
      }),
    });
    return session;
  }

  /**
   * Try and get the currentUser out of local cache, and if it's not there, get one from the database.
   *
   * @privateRemarks
   * Since the getUser command is async, we can't collapse this function yet in the currentUser getter / setter.
   * It would be nice to make sure that currentUser is always set, so that we can just use the getters and the setters.
   *
   * @returns User
   */
  getActiveUser() {
    return this.currentUser;
  }

  /**
   * Return the favorite objects of the user.
   *
   * @example
   * Example of return value:
   * ```
   *  [
   *   { type: 'TEAM', id: '01G6JK4AHE7FP6CXBSS748A68Z' },
   *   { type: 'TEAM', id: '01G6N31TWQ9BFPTQEWQDV7BE08' },
   *   { type: 'INITIATIVE', id: '01G4FNPAJ5NB9J9EN4Z2GFX06M' },
   *   { type: 'INITIATIVE', id: '01G51QH4WG8CSHHA3B2RC4Y93W' },
   * ];
   * ```
   */
  get favorites(): FavoriteRef[] {
    return (this.currentUser?.get?.('favorites') as FavoriteRef[]) ?? [];
  }

  async load() {
    if (this.session.isAuthenticated) {
      let person: CurrentUserRecord | undefined;
      try {
        person = await this.getUser();
      } catch (err) {
        // A non-404 error (e.g. 401, 500, network failure) — abort without
        // creating a duplicate person record.
        Sentry.captureException(err, {
          extra: {
            reason:
              'Fetch /me failed with a non-404 error — skipping person creation to prevent duplicates',
          },
        });
        return;
      }
      if (person?.id === undefined) {
        const newPerson = await this.createUser();
        await this.claimUser(newPerson.id as string);
      }
    }
  }

  @action
  async getUser() {
    let person = null;
    try {
      person = (await this.store.queryRecord('person', {
        me: true,
      })) as CurrentUserRecord;
    } catch (error) {
      const err = error as {
        errors?: { status?: unknown }[];
        status?: unknown;
        response?: { status?: unknown };
      };
      const status =
        err?.errors?.[0]?.status ?? err?.status ?? err?.response?.status;
      if (String(status) === '404') {
        // No person record exists yet — caller will create one
        return loggedOutUser;
      }
      // For all other errors (401, 403, 5xx, network) do NOT trigger user
      // creation. Re-throw so the caller can decide what to do.
      throw error;
    }
    this.currentUser = person;
    if (this.favorites) this.formatFavorites(this.favorites);
    return this.currentUser;
  }

  async getRecord(type: string, id: string) {
    let record = null;
    record = await this.store.peekRecord(type, id);
    if (!record)
      record = this.store
        .findRecord(
          type,
          id,
          type === 'initiative' ? { include: 'all' } : undefined,
        )
        .catch(() => false);
    return record;
  }

  formatFavorites(favorites: FavoriteRef[]) {
    const length = favorites.length;
    for (let i = 0; i < length; i++) {
      const { type, id } = favorites[i]!;
      this.getRecord(type.toLowerCase(), id)
        .then((record) => {
          if (record) {
            record.favorite = true;
          }
        })
        .catch(() => null);
    }
  }

  /**
   * Create a Persona in the application based on the email address used to sign in.
   *
   * @remarks
   * If sombody logs in, but doesn't have a persona in the system yet. We create a person using their emailadres to give them a name.
   * The person can then later edit this themselves.
   *
   * @returns Person
   */
  async createUser(email?: string) {
    let name =
      email ?? authDetails(this.session)!.email!.split('@')[0]!.split('+')[0]!;
    name = name.charAt(0).toUpperCase() + name.slice(1);
    const personCreate = this.store.createRecord('person', {
      name,
    }) as Model;
    const response = await personCreate.save();
    return response;
  }

  @action
  goToLogout() {
    this.router.transitionTo('/logout');
  }

  /**
   * Attach your CognitoID to a user in the Database
   *
   * @remarks
   * We have 2 types of "users": Users & Personas.
   *
   * The first type of users are in AWS Cognito, it's where your username (email) and password are stored.
   * The application doesn't really know anything about that.
   *
   * The second type of "user" are the personals we create in the application. It you, or your colleague, Bill or Jean.
   * These personas can exist even if there is no "User" attached in Cognito. You can actually assign items to "Bob"
   * even when Bob has never logged into Scaleflow.
   *
   * However, when Bob DOES log into scaleflow using his User in Cognito, he can come in and say "Hey...that's me, I'm that Bob!"
   * That'se why this function is called "ClaimUser" - Bob (Cognito) can claim (Bob) in the application.
   *
   * @param id - the ID of the user in the backend.
   * @returns user
   */
  async claimUser(id: string) {
    const claimUser = await this.adapter.claimUser(id);
    if (claimUser) {
      const person = (await this.store.queryRecord('person', {
        me: true,
      })) as CurrentUserRecord;
      this.currentUser = person;
      return this.currentUser;
    }
    return claimUser;
  }

  /**
   * Clear the current user for a logged out User
   *
   */
  clear() {
    this.currentUser = loggedOutUser;
  }

  get favoriteTeams() {
    return this._currentUser.favoriteTeams;
  }

  get favoriteInitiatives() {
    return this._currentUser.favoriteInitiatives;
  }
}
